import type { UseFormReturn } from "react-hook-form";
import { FormProvider } from "react-hook-form";
import { z } from "zod";

import { CustomButton } from "@/components/atoms";
import { TextareaControl } from "@/components/molecules";
import {
  ARABIC_TEXT_WITH_NUMBERS_AND_SIGNS_REGEX,
  ENGLISH_TEXT_WITH_NUMBERS_AND_SIGNS_REGEX,
} from "@/utils/constants/arabicRegex";

const requiredText = z.string().trim().min(1, "This field is required");
const requiredEnglishText = requiredText.regex(
  ENGLISH_TEXT_WITH_NUMBERS_AND_SIGNS_REGEX,
  "Only English letters, numbers, and special characters are allowed."
);
const requiredArabicText = requiredText.regex(
  ARABIC_TEXT_WITH_NUMBERS_AND_SIGNS_REGEX,
  "Only Arabic characters, numbers, and special characters are allowed."
);

export const faqFormSchema = z.object({
  questionEn: requiredEnglishText,
  answerEn: requiredEnglishText,
  questionAr: requiredArabicText,
  answerAr: requiredArabicText,
});

export type FaqFormValues = z.infer<typeof faqFormSchema>;

export const emptyFaqFormValues: FaqFormValues = {
  questionEn: "",
  answerEn: "",
  questionAr: "",
  answerAr: "",
};

interface FaqFormProps {
  form: UseFormReturn<FaqFormValues>;
  isPending: boolean;
  submitLabel: string;
  testIdPrefix: string;
  onCancel: () => void;
  onSubmit: (values: FaqFormValues) => void;
}

const FaqForm = ({
  form,
  isPending,
  submitLabel,
  testIdPrefix,
  onCancel,
  onSubmit,
}: FaqFormProps) => {
  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = form;

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <div className="pt-2">
          <TextareaControl<FaqFormValues>
            formControl={control}
            nameField="questionEn"
            label="Question (English)"
            height="h-20"
            data-testid={`${testIdPrefix}-question-english`}
          />
          <TextareaControl<FaqFormValues>
            formControl={control}
            nameField="answerEn"
            label="Answer (English)"
            height="h-32"
            data-testid={`${testIdPrefix}-answer-english`}
          />
          <TextareaControl<FaqFormValues>
            formControl={control}
            nameField="questionAr"
            label="Question (Arabic)"
            height="h-20"
            dir="rtl"
            data-testid={`${testIdPrefix}-question-arabic`}
          />
          <TextareaControl<FaqFormValues>
            formControl={control}
            nameField="answerAr"
            label="Answer (Arabic)"
            height="h-32"
            dir="rtl"
            data-testid={`${testIdPrefix}-answer-arabic`}
          />
        </div>

        <div className="flex gap-3">
          <CustomButton
            variant="light"
            type="button"
            className="flex-1"
            onClick={onCancel}
            disabled={isPending}
          >
            Cancel
          </CustomButton>
          <CustomButton
            type="submit"
            className="flex-1"
            disabled={!isValid || isPending}
            isPending={isPending}
            data-testid={`${testIdPrefix}-submit-button`}
          >
            {submitLabel}
          </CustomButton>
        </div>
      </form>
    </FormProvider>
  );
};

export default FaqForm;
